import React, { useEffect, useRef, useState } from "react";
import { string, object } from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import InputLayout from "../../../components/uikit/components/inputLayout/InputLayout";
import { clarityNav } from "../../../services/api/clarityNav/apiService";
import { formFields } from "../../../constants/projectDetailForm";
import {
  checkDataisObject,
  createProjectHeading,
  getRouteId,
  projectErrorMessages,
  SNACKBAR_TYPES,
} from "../../../constants/constant";
import ButtonComponent from "../../../components/uikit/components/Button/ButtonComponent";
import {
  setCurrentProjectDetail,
  setProjectData,
  setTotalProjects,
  setStepcompleted,
  resetCurrentMVOCDetail,
  resetCurrentProjectDetail,
} from "../../../features/product/projectSlice";
import { routespath } from "../../../constants";
import {
  closeSnackbar,
  openSnackbar,
} from "../../../features/snackbar/snackbarslice";
import { useLocation, useNavigate } from "react-router-dom";

const ProjectDetailsPage = ({ onDisableMVOCChange }) => {
  // Import hooks from React and React Router
  const location = useLocation(); // Gets the current location object from React Router
  const Project_id = getRouteId(location.pathname); // Extracts the project ID from the URL path
  const prevNameRef = useRef(""); // Stores the previous project name persistently across renders

  // Navigation hook
  const navigate = useNavigate(); // Hook for programmatic navigation within the application

  // Dispatch hook
  const dispatch = useDispatch(); // Hook to dispatch actions to the Redux store

  // State to manage error messages related to project name existence
  const [projectNameExistErrMsg, setProjectNameExistErrMsg] = useState("");

  // Selector to get the current project details from Redux store
  const currentProjectDetail = useSelector(
    (state) => state?.product?.currentProjectDetail
  );

  // Filtering form fields based on their section
  const upFields = formFields.filter((field) => field.section === "up") || []; // Fields for the upper section
  const downFields =
    formFields.filter((field) => field.section === "down") || []; // Fields for the lower section

  // State to store the current project ID
  const [projectId, setProjectId] = useState(null);

  // Selectors to fetch user project details from Redux
  const userProjectsCount = useSelector((state) => state.product.totalProjects); // Total number of projects
  const userProjectsData = useSelector((state) => state.product.projectsData); // Project data array

  // Selector to get the current step completion status from Redux
  const stepper = useSelector((state) => state?.product?.stepCompleted);

  // API call hooks for handling project-related operations
  const [createProject] = clarityNav.useCreateProjectMutation(); // Mutation hook to create a project
  const [nameValidation] = clarityNav.useProjectNameValidationMutation(); // Mutation hook to validate project name
  const [fetchProjectData] = clarityNav.endpoints.getProjectDataFromProjectId.useLazyQuery(); // Lazy query to fetch project data by ID

  // reset state of current MVOC detail
  useEffect(() => {
    dispatch(resetCurrentMVOCDetail());
  }, []);

  /**
   * Handles the name check for the project.
   *
   * This function performs the following steps:
   * 1. Triggers the formik handleBlur event for the "name" field.
   * 2. Checks if the projectId exists and if the current formik name value matches the current project detail name.
   *    - If they match, it clears the project name existence error message and returns.
   * 3. If the formik name value has changed from the previous name reference:
   *    - Constructs a payload with the new name.
   *    - Dispatches actions to reset the current project and MVOC details.
   *    - Calls the nameValidation function with the payload.
   *    - Updates the previous name reference.
   *    - Sets the project name existence error message based on the response.
   *
   * @async
   * @function handleNameChek
   * @returns {Promise<void>} A promise that resolves when the name check is complete.
   */
  const handleNameChek = async () => {
    formik.handleBlur({ target: { name: "name" } });
    if (projectId && formik.values.name === currentProjectDetail.name) {
      setProjectNameExistErrMsg("");
      return;
    }
    if (formik.values.name && formik.values.name !== prevNameRef.current) {
      try {
        const payload = {
          name: formik.values.name,
        };
        dispatch(resetCurrentProjectDetail());
        dispatch(resetCurrentMVOCDetail());
        const response = await nameValidation(payload);
        prevNameRef.current = formik.values.name; // Update previous name reference
        if (response.data.result) {
          setProjectNameExistErrMsg(response?.data?.message);
        } else {
          setProjectNameExistErrMsg("");
        }
      } catch (error) {}
    }
  };

  // validation schema for form
  /**
   * Validation schema for the project details form.
   *
   * @type {Object}
   * @property {string} name - The name of the project. This field is required.
   * @property {string} project_id - The ID of the project.
   * @property {string} client_name - The name of the client. This field is required.
   */
  const validationSchema = object().shape({
    name: string().required(
      projectErrorMessages.PROJECT_NAME_MANDATORY_ERROR_MESSAGE
    ),
    project_id: string(),
    client_name: string().required("Client name is required"),
  });

  // on mount get project details on the baios of project id from location
  /**
   * Updates the project details by fetching data from the server if necessary
   * and setting the form values accordingly.
   *
   * If the project ID is valid and not equal to the route path for creating project details,
   * it checks if the current project detail object has any keys. If it does, it sets the form values
   * to the current project detail. Otherwise, it fetches the project data from the server, sets the form values
   * to the fetched data, and updates the current project detail in the state.
   *
   * If an error occurs during the fetch, it dispatches an action to open a snackbar with the error message
   * and closes the snackbar after 10 seconds.
   *
   * @async
   * @function updateProjectDetails
   * @returns {Promise<void>} A promise that resolves when the project details have been updated.
   */
  useEffect(() => {
    const updateProjectDetails = async () => {
      if (Project_id && Project_id != routespath.createProjectDetails) {
        if (Object?.keys(currentProjectDetail)?.length > 0) {
          formik?.setValues(currentProjectDetail);
        } else {
          try {
            const { data } = await fetchProjectData(Project_id);
            formik?.setValues(data?.result);
            dispatch(setCurrentProjectDetail(data?.result));
          } catch (error) {
            console.log("error", error);
          }
        }
      }
    };
    updateProjectDetails();
  }, []);

  // formik submit handler and for update project data and project count in redux (userProjectsData)
  /**
   * Initializes the formik object with initial values, validation schema, and submit handler.
   * Handles the form submission and updates the project data in the Redux store.
   * @property {boolean} validateOnChange - Whether to validate the form on change.
   * @property {boolean} validateOnBlur - Whether to validate the form on blur.
   * @property {object} validationSchema - The validation schema for the form.
   * @property {function} onSubmit - The function to call when the form is submitted.
   * @property {function} onSubmit.values - The form values.
   * @property {function} onSubmit.returns - Returns nothing if projectNameExistErrMsg is true.
   * @property {function} onSubmit.throws - Throws an error if the project creation fails.
   */
  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      client_name: "",
    },
    validateOnChange: true,
    validateOnBlur: false,
    validationSchema,
    onSubmit: async (values) => {
      if (projectNameExistErrMsg) {
        return;
      }
      try {
          onDisableMVOCChange(false);
          const response = await createProject(values).unwrap();
          dispatch(
            openSnackbar({
              message: `Project Created successfull!!`,
              type: SNACKBAR_TYPES.SUCCESS,
            })
          );
          setTimeout(() => {
            dispatch(closeSnackbar());
          }, 10000);
          if (response?.success && checkDataisObject(response?.result)) {
            dispatch(setTotalProjects(userProjectsCount + 1));
            dispatch(setProjectData([...userProjectsData, response?.result]));
            dispatch(setCurrentProjectDetail(response?.result));
            dispatch(resetCurrentMVOCDetail());
            dispatch(setStepcompleted(1));
            navigate(
              `${routespath[0].createProjectDetails}/${response?.result?.id}`
            );
          }
      } catch (error) {}
    },
  });

  /**
   * Handles the change event for the project name input field.
   * Clears any existing error message and updates the formik field value.
   *
   * @param {Object} event - The event object from the input field.
   * @param {string} field - The name of the formik field to update.
   */
  const handleNameChange = (event, field) => {
    setProjectNameExistErrMsg("");
    formik.setFieldValue(field, event.target.value);
  };

  // Reset form when navigating back to project creation
  /**
   * Resets the form and current MVOC detail on navigation.
   * If the Project_id is not defined or matches the create project details route,
   * the form will be reset.
   *
   * @function resetFormOnNavigation
   * @returns {void}
   */
  useEffect(() => {
    const resetFormOnNavigation = () => {
      if (!Project_id || Project_id === routespath[0]?.createProjectDetails) {
        formik.resetForm(); // Reset the form if on the create project page
      }
      dispatch(resetCurrentMVOCDetail());
    };

    resetFormOnNavigation();
  }, [location.pathname]);

  return (
    <div>
      <form
        className="container-fluid pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="card w-100 border-none">
          <span className="project-title-text">
            {createProjectHeading.PROJECT_DETAILS}
          </span>
          {/* up details */}
          <div className="w-[100%] flex gap-[20px]" key={1}>
            {upFields.map((field) => (
              <div className="mb-[10px] w-[50%]" key={field.id}>
                <p className="font-semibold text-sm text-primaryBlack mb-2">
                  {field.label}
                  {field.isMandatory && (
                    <span className="text-red-500 pl-[2px]">*</span>
                  )}
                  {field.otherLabel && (
                    <span className="pl-[2px] text-sm font-medium text-optionalTextColor ">
                      ({field.otherLabel})
                    </span>
                  )}
                </p>
                <InputLayout
                  placeholder={
                    stepper === 1 || Project_id ? "" : field?.placeholder || ""
                  }
                  onBlur={field?.id === "name" ? handleNameChek : undefined}
                  className="text-[#25282B] focus-visible:outline-none border border-formInputBorder rounded-md w-[100%] font-normal text-sm pl-1 pt-2.5 pb-2.5"
                  id={field?.id}
                  onChange={
                    field?.id === "name"
                      ? (e) => handleNameChange(e, field?.id)
                      : formik?.handleChange
                  }
                  value={formik?.values?.[field?.id] ?? ""}
                  disable={!!Project_id}
                />
                {field?.id == "name" ? (
                  projectNameExistErrMsg ? (
                    <div className="flex items-center mt-2">
                      <p className="pl-[3px] text-xs  font-semibold text-errorMessageColor">
                        {projectNameExistErrMsg}
                      </p>
                    </div>
                  ) : (
                    <>
                      {" "}
                      {formik.touched[field.id] &&
                        formik.errors[field.id] &&
                        field.id !== "group_name" && (
                          <div className="flex items-center mt-2">
                            <p className="pl-[3px] text-xs font-semibold text-errorMessageColor">
                              {formik.errors[field.id]}
                            </p>
                          </div>
                        )}
                    </>
                  )
                ) : (
                  <>
                    {" "}
                    {formik.touched[field.id] &&
                      formik.errors[field.id] &&
                      field.id !== "group_name" && (
                        <div className="flex items-center mt-2">
                          <p className="pl-[3px] text-xs font-semibold text-errorMessageColor">
                            {formik.errors[field.id]}
                          </p>
                        </div>
                      )}
                  </>
                )}
              </div>
            ))}
          </div>
          {/* down details */}
          <div className="w-[100%] flex gap-[20px]" key={2}>
            {downFields.map((field) => (
              <div className="w-[100%]" key={field.id}>
                <p className="font-semibold text-sm text-primaryBlack mb-2">
                  {field.label}
                  {field.isMandatory && (
                    <span className="text-red-500 pl-[2px]">*</span>
                  )}
                  {field.otherLabel && (
                    <span className="pl-[2px] text-sm font-medium text-optionalTextColor ">
                      ({field.otherLabel})
                    </span>
                  )}
                </p>
                <InputLayout
                  placeholder={
                    stepper === 1 || Project_id ? "" : field?.placeholder || ""
                  }
                  className="text-[#25282B] focus-visible:outline-none border border-formInputBorder rounded-md w-[100%] h-16 font-normal text-sm pl-1 pt-2.5 pb-2.5"
                  id={field.id}
                  onChange={formik.handleChange}
                  value={formik?.values?.[field?.id] ?? ""}
                  disable={!!Project_id}
                />
              </div>
            ))}
          </div>
        </div>
        {!Project_id && (
          <div className="d-flex w-100 my-3 justify-content-end">
            <ButtonComponent
              variant="contained"
              onClick={formik.handleSubmit}
              btnType="primary"
              text="Save & Select Insight Source"
              className="btnPrimary"
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default ProjectDetailsPage;
